'use client';

import { c as _c } from "react/compiler-runtime";
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { Suspense, useDeferredValue, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES } from '@/constants';
import { formatWon, PAYOUT_DAY } from '../../_utils/labels';
import type { PartnersQueries } from '../../_utils/queries';
import { contextOf } from '../../_utils/queries';
import type { ReportFilter } from '../../_utils/report/report';
import { dayCountOf, filterOfSearchParams, filterToQuery, firstDateOf, rangeDatesOf, rangeLabelOf, replaceFilterUrl, resolvedFilterOf, todayOf, unitOf } from '../../_utils/report/report';
import type { PartnersReport, ReportUnit } from '../../_utils/types';
import { SectionTitle } from '../home/HomeBlocks';
import { SectionLoadError } from '../SectionLoadError';
import { ReportFilterBar } from './ReportFilterBar';
import { REPORT_SERIES, ReportLineGraph, UNIT_TITLE } from './ReportLineGraph';
interface ReportBodyProps {
  report: PartnersReport | null;
  reportFilter: ReportFilter;
  unit: ReportUnit;
  filterBar: ReactNode;
}
const ReportBody = t0 => {
  const $ = _c(73);
  const {
    report,
    reportFilter,
    unit,
    filterBar
  } = t0;
  const t1 = report?.periodStart ?? reportFilter.start;
  let metrics;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== filterBar || $[1] !== report || $[2] !== reportFilter || $[3] !== t1 || $[4] !== unit) {
    const shownFilter = {
      ...reportFilter,
      start: t1
    };
    let t9;
    if ($[13] !== report?.clickCount) {
      t9 = report?.clickCount.toLocaleString("ko-KR");
      $[13] = report?.clickCount;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== t9) {
      t10 = {
        label: "\uD074\uB9AD\uC218",
        value: t9
      };
      $[15] = t9;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] !== report?.donationCount) {
      t11 = report?.donationCount.toLocaleString("ko-KR");
      $[17] = report?.donationCount;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    let t12;
    if ($[19] !== t11) {
      t12 = {
        label: "\uAE30\uBD80\uC218",
        value: t11
      };
      $[19] = t11;
      $[20] = t12;
    } else {
      t12 = $[20];
    }
    let t13;
    if ($[21] !== report) {
      t13 = report && `${Number(report.conversionRate.toFixed(2))}%`;
      $[21] = report;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== t13) {
      t14 = {
        label: "\uAE30\uBD80\uC804\uD658\uC728",
        value: t13
      };
      $[23] = t13;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    let t15;
    if ($[25] !== t10 || $[26] !== t12 || $[27] !== t14) {
      t15 = [t10, t12, t14];
      $[25] = t10;
      $[26] = t12;
      $[27] = t14;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    metrics = t15;
    const payoutBoxes = [{
      label: "\uC9C0\uAE09 \uC608\uC815 \uC6F0\uD3EC\uC778\uD2B8",
      amount: report?.scheduledRewardAmount,
      className: "text-Gray-900"
    }, {
      label: "\uC9C0\uAE09 \uC644\uB8CC \uC6F0\uD3EC\uC778\uD2B8",
      amount: report?.paidRewardAmount,
      className: "text-B-550"
    }];
    let t16;
    if ($[29] !== report) {
      t16 = report ? formatWon(report.totalRewardAmount) : "-";
      $[29] = report;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== t16) {
      t17 = <dd className="m-0 font-800 text-Gray-900 typo-h5">{t16}</dd>;
      $[31] = t16;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const t18 = <dl className="m-0 flex flex-col gap-4"><dt className="break-keep font-600 text-Gray-600 typo-caption1">{rangeLabelOf(shownFilter)} 이 기간 전체 수익</dt>{t17}</dl>;
    let t19;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <CustomLink href={DONATION_ROUTES.PARTNERS_REWARDS.pathname} className={cn(INTERACTIVE_SURFACE, "group flex size-30 shrink-0 items-center justify-center rounded-xxsmall font-700 text-Gray-600 typo-caption1 xl:size-auto xl:gap-2 xl:px-4 xl:py-8")}><span className="sr-only xl:not-sr-only">정산내역</span><SvgChevronright aria-hidden={true} className="size-12 transition-transform group-hover:translate-x-1 group-focus-visible:translate-x-1" /></CustomLink>;
      $[33] = t19;
    } else {
      t19 = $[33];
    }
    let t20;
    if ($[34] !== t18) {
      t20 = <div className="flex items-center justify-between gap-8">{t18}{t19}</div>;
      $[34] = t18;
      $[35] = t20;
    } else {
      t20 = $[35];
    }
    let t21;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <p className="m-0 text-Gray-500 typo-caption1">웰로에서 완료한 기부만 웰포인트로 적립됩니다.<br />당월 1일~말일 누적 → 익월 {PAYOUT_DAY}일 지급</p>;
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    let t22;
    if ($[37] !== t20) {
      t22 = <div className="flex flex-col gap-4">{t20}{t21}</div>;
      $[37] = t20;
      $[38] = t22;
    } else {
      t22 = $[38];
    }
    const t23 = payoutBoxes.map(_temp);
    let t24;
    if ($[39] !== t23) {
      t24 = <dl className="m-0 flex flex-col gap-8">{t23}</dl>;
      $[39] = t23;
      $[40] = t24;
    } else {
      t24 = $[40];
    }
    let t25;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <p className="m-0 text-Gray-500 typo-caption1">기간별 보고서는 5분마다 업데이트돼요.</p>;
      $[41] = t25;
    } else {
      t25 = $[41];
    }
    let t26;
    if ($[42] !== t22 || $[43] !== t24) {
      t26 = <div className="flex flex-col gap-12 rounded-small bg-Gray-25 p-20 xl:gap-20">{t22}{t24}{t25}</div>;
      $[42] = t22;
      $[43] = t24;
      $[44] = t26;
    } else {
      t26 = $[44];
    }
    if ($[45] !== filterBar || $[46] !== t26) {
      t8 = <div className="flex flex-col gap-16">{filterBar}{t26}</div>;
      $[45] = filterBar;
      $[46] = t26;
      $[47] = t8;
    } else {
      t8 = $[47];
    }
    t7 = "flex flex-col gap-16";
    t6 = "flex flex-col gap-12";
    t4 = "flex items-center justify-between gap-8";
    const t27 = UNIT_TITLE[unit];
    if ($[48] !== t27) {
      t5 = <h4 className="m-0 font-700 text-Gray-900 typo-body4">{t27} 클릭수 · 기부수</h4>;
      $[48] = t27;
      $[49] = t5;
    } else {
      t5 = $[49];
    }
    t2 = "text-Gray-500 typo-caption1";
    t3 = rangeDatesOf(shownFilter).join(" ~ ");
    $[0] = filterBar;
    $[1] = report;
    $[2] = reportFilter;
    $[3] = t1;
    $[4] = unit;
    $[5] = metrics;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    metrics = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
  }
  let t9;
  if ($[50] !== t2 || $[51] !== t3) {
    t9 = <span className={t2}>{t3}</span>;
    $[50] = t2;
    $[51] = t3;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== t4 || $[54] !== t5 || $[55] !== t9) {
    t10 = <div className={t4}>{t5}{t9}</div>;
    $[53] = t4;
    $[54] = t5;
    $[55] = t9;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== report || $[58] !== unit) {
    t11 = report?.donationCount === 0 && report.clickCount === 0 ? <p className="m-0 flex flex-col items-center gap-4 break-keep rounded-small bg-Gray-25 px-20 py-28 text-center typo-body4"><span className="font-600 text-Gray-700">아직 클릭·기부 실적이 없어요</span><span className="text-Gray-550">내 링크로 들어온 클릭과 기부가 여기에 그래프로 쌓여요.</span></p> : <><ReportLineGraph device="mobile" className="flex xl:hidden" series={report?.series ?? []} unit={unit} /><ReportLineGraph device="desktop" className="hidden xl:flex" series={report?.series ?? []} unit={unit} /><div className="flex justify-center gap-16 font-600 text-Gray-700 typo-caption1">{REPORT_SERIES.map(_temp2)}</div></>;
    $[57] = report;
    $[58] = unit;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== t10 || $[61] !== t11 || $[62] !== t6) {
    t12 = <div className={t6}>{t10}{t11}</div>;
    $[60] = t10;
    $[61] = t11;
    $[62] = t6;
    $[63] = t12;
  } else {
    t12 = $[63];
  }
  let t13;
  if ($[64] !== metrics) {
    t13 = <dl className="m-0 grid grid-cols-3 gap-8">{metrics.map(_temp3)}</dl>;
    $[64] = metrics;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== t12 || $[67] !== t13 || $[68] !== t7) {
    t14 = <div className={t7}>{t12}{t13}</div>;
    $[66] = t12;
    $[67] = t13;
    $[68] = t7;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  let t15;
  if ($[70] !== t14 || $[71] !== t8) {
    t15 = <>{t8}{t14}</>;
    $[70] = t14;
    $[71] = t8;
    $[72] = t15;
  } else {
    t15 = $[72];
  }
  return t15;
};
interface ReportDataProps extends Omit<ReportBodyProps, 'report'> {
  queries: PartnersQueries;
}
const ReportData = t0 => {
  const $ = _c(9);
  let bodyProps;
  let t1;
  let t2;
  if ($[0] !== t0) {
    const {
      queries,
      ...t3
    } = t0;
    bodyProps = t3;
    t2 = useSuspenseQuery;
    t1 = queries.report(filterToQuery(bodyProps.reportFilter, bodyProps.unit));
    $[0] = t0;
    $[1] = bodyProps;
    $[2] = t1;
    $[3] = t2;
  } else {
    bodyProps = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = {
      ...t1,
      select: contextOf
    };
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: report
  } = t2(t3);
  let t4;
  if ($[6] !== bodyProps || $[7] !== report) {
    t4 = <ReportBody report={report} {...bodyProps} />;
    $[6] = bodyProps;
    $[7] = report;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
interface ReportSectionProps {
  queries: PartnersQueries;
  initialFilter?: ReportFilter;
  isExample: boolean;
  children?: (filter: ReportFilter) => ReactNode;
}
export const ReportSection = t0 => {
  const $ = _c(36);
  const {
    queries,
    initialFilter,
    isExample,
    children
  } = t0;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== initialFilter || $[1] !== searchParams) {
    t1 = () => initialFilter ?? filterOfSearchParams(Object.fromEntries(searchParams));
    $[0] = initialFilter;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [filter, setFilter] = useState(t1);
  const deferredFilter = useDeferredValue(filter);
  let t2;
  if ($[3] !== queries) {
    t2 = queries.links({
      to: todayOf()
    });
    $[3] = queries;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      ...t2,
      select: _temp4
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: t4
  } = useQuery(t3);
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== children || $[8] !== deferredFilter || $[9] !== filter || $[10] !== isExample || $[11] !== queries || $[12] !== t4) {
    const firstDate = t4 === undefined ? todayOf() : t4;
    const appliedFilter = resolvedFilterOf(filter, firstDate);
    const reportFilter = resolvedFilterOf(deferredFilter, firstDate);
    let t10;
    if ($[18] !== isExample) {
      t10 = next => {
        setFilter(next);
        if (!isExample) {
          replaceFilterUrl(next);
        }
      };
      $[18] = isExample;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    const bodyProps = {
      reportFilter,
      unit: unitOf(dayCountOf(reportFilter.start, reportFilter.end)),
      filterBar: <ReportFilterBar key={`${appliedFilter.channel}~${appliedFilter.start}~${appliedFilter.end}~${appliedFilter.isAll}`} filter={appliedFilter} onApply={t10} firstDate={firstDate} />
    };
    t8 = "flex flex-col gap-12";
    if ($[20] !== isExample) {
      t9 = <SectionTitle title={"\uAE30\uAC04\uBCC4 \uBCF4\uACE0\uC11C"} isExample={isExample} />;
      $[20] = isExample;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t5 = "flex flex-col gap-20 rounded-medium border border-Gray-200 bg-Gray-white p-20 shadow-base";
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <SectionLoadError />;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== bodyProps || $[24] !== queries) {
      t12 = <ReportData queries={queries} {...bodyProps} />;
      $[23] = bodyProps;
      $[24] = queries;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    const t13 = <Suspense fallback={<ReportBody report={null} {...bodyProps} />}>{t12}</Suspense>;
    if ($[26] !== t13) {
      t6 = <ErrorBoundary fallback={t11}>{t13}</ErrorBoundary>;
      $[26] = t13;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    t7 = children ? <div className="border-t border-Gray-200 pt-20">{children(reportFilter)}</div> : null;
    $[7] = children;
    $[8] = deferredFilter;
    $[9] = filter;
    $[10] = isExample;
    $[11] = queries;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[28] !== t5 || $[29] !== t6 || $[30] !== t7) {
    t10 = <section className={t5}>{t6}{t7}</section>;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t8 || $[34] !== t9) {
    t11 = <div className={t8}>{t9}{t10}</div>;
    $[32] = t10;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
function _temp(box) {
  return <div key={box.label} className="flex flex-col gap-2 rounded-xsmall bg-Gray-white px-12 py-8"><dt className="font-600 text-Gray-600 typo-caption1">{box.label}</dt><dd className={cn("m-0 font-700 typo-body3", box.className)}>{box.amount === undefined ? "-" : formatWon(box.amount)}</dd></div>;
}
function _temp2(line) {
  return <span key={line.key} className="flex items-center gap-4"><span className={cn("h-3 w-12 rounded-pill", line.swatch)} />{line.label}</span>;
}
function _temp3(metric) {
  return <div key={metric.label} className="flex flex-col gap-2 rounded-small border border-Gray-200 p-12"><dt className="whitespace-nowrap font-600 text-Gray-600 typo-caption1">{metric.label}</dt><dd className="m-0 font-700 text-Gray-900 typo-body3">{metric.value ?? "-"}</dd></div>;
}
function _temp4(response) {
  return firstDateOf(contextOf(response).contents);
}